import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Eraser, Loader2 } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";

const ClearTestDataButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { isSuperAdmin } = useUserRole();

  if (!isSuperAdmin) return null;

  const handleClear = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("clear-qa-test-data");
    setLoading(false);
    setOpen(false);

    if (error) {
      toast({
        title: "Failed to clear test data",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    const deleted = (data as { deleted?: number } | null)?.deleted ?? 0;
    toast({
      title: deleted === 0 ? "No test data found" : "Test data cleared",
      description:
        deleted === 0
          ? "No appointments with notes starting with 'QA:' were found."
          : `Deleted ${deleted} appointment${deleted === 1 ? "" : "s"} with notes starting with 'QA:'.`,
    });
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        title="Delete every appointment whose notes start with 'QA:'"
      >
        <Eraser size={14} className="mr-2" />
        Clear my test data
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear QA test appointments?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes every appointment whose notes start with{" "}
              <code className="bg-muted px-1 py-0.5 rounded text-xs">QA:</code>. Real
              patient appointments are not affected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClear} disabled={loading}>
              {loading && <Loader2 size={14} className="mr-2 animate-spin" />}
              Delete test appointments
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ClearTestDataButton;
